import { Checkout } from './components/componentTypes/checkout';
import { Express } from './components/componentTypes/express';
import { Messages } from './components/componentTypes/messages';
import { Setup } from './components/componentTypes/setup';
import type {
  SalesforcePaymentsCheckoutConfig,
  SalesforcePaymentsConfig,
  SalesforcePaymentsExpressConfig,
  SalesforcePaymentsSetupConfig,
  SDKConfig,
} from './types/config';
import type { PaymentRequest, PaymentIntentRequest, PaymentSetupRequest } from './types/paymentRequest';
import type { PaymentResponse } from './types/paymentResponse';
import type { PaymentMethodSet, ExpressType } from './types/paymentMethodSet';
import type { Vendor } from './types/paymentMethodSet';
import { getVendorHandler } from './util/load';
import './styles.css';
import SfpContext from './sfp-context';
import type { Metadata } from './types/metadata';
import { MetadataReader } from './util/metadata-reader';
import { checkAmount, checkCurrency, checkRequired, checkFunction } from './util/preconditions';

export default class Sfp {
  context: SfpContext;

  /**
   * Constructs a Salesforce Payments object. Use a Salesforce Payments object to construct and mount payment components.
   * @param {SDKConfig} [config] - SDK configuration information.
   * @class SFPayments
   * @public
   */
  constructor(config: SDKConfig) {
    if (config?.report) {
      checkFunction(config.report, 'config.report');
    }
    this.context = new SfpContext(config);
  }

  /**
   * Constructs a checkout payment sheet component and mounts it in the DOM.
   * @param {Metadata} metadata - Payment method metadata.
   * @param {PaymentMethodSet} paymentMethodSet - Payment method set containing information about the payment methods to present, and their related vendor data.
   * @param {SalesforcePaymentsCheckoutConfig} config - Configuration of the checkout component.
   * @param {PaymentIntentRequest} paymentRequest - Request to make payment.
   * @param {string|HTMLElement} elementOrSelectorToMountTo - HTML element in which to mount the component, or a CSS selector matching that element.
   * @returns {Checkout} - New checkout component instance.
   * @memberof SFPayments
   * @instance
   * @public
   */
  checkout(
    metadata: Metadata,
    paymentMethodSet: PaymentMethodSet,
    config: SalesforcePaymentsCheckoutConfig,
    paymentRequest: PaymentIntentRequest,
    elementOrSelectorToMountTo: string | HTMLElement
  ): Checkout {
    try {
      const checkoutComponent = new Checkout(
        this.context,
        new MetadataReader(checkRequired(metadata, 'metadata')),
        checkRequired(paymentMethodSet, 'paymentMethodSet'),
        this.checkConfig(config),
        this.checkPaymentIntentRequest(paymentRequest),
        checkRequired(elementOrSelectorToMountTo, 'elementOrSelectorToMountTo')
      );

      // kick off initialization
      checkoutComponent.initialize();

      //return the checkout component to caller to make APIs available
      return checkoutComponent;
    } catch (err) {
      this.context.report({
        code: 'CHECKOUT_COMPONENT_CREATION_ERROR',
        type: 'COMPONENT_CREATION_ERROR',
        message: `Error creating checkout component: ${err}`,
      });
      throw err;
    }
  }

  /**
   * Constructs a saved payment method setup payment sheet component and mounts it in the DOM.
   * @param {Metadata} metadata - Payment method metadata.
   * @param {PaymentMethodSet} paymentMethodSet - Payment method set containing information about the payment methods to present, and their related vendor data.
   * @param {SalesforcePaymentsSetupConfig} config - Configuration of the setup component.
   * @param {PaymentSetupRequest} paymentRequest - Request to setup saved payment method.
   * @param {string|HTMLElement} elementOrSelectorToMountTo - HTML element in which to mount the component, or a CSS selector matching that element.
   * @returns {Setup} - New setup component instance.
   * @memberof SFPayments
   * @instance
   * @public
   */
  setup(
    metadata: Metadata,
    paymentMethodSet: PaymentMethodSet,
    config: SalesforcePaymentsSetupConfig,
    paymentRequest: PaymentSetupRequest,
    elementOrSelectorToMountTo: string | HTMLElement
  ): Setup {
    try {
      const setupComponent = new Setup(
        this.context,
        new MetadataReader(checkRequired(metadata, 'metadata')),
        checkRequired(paymentMethodSet, 'paymentMethodSet'),
        this.checkConfig(config),
        this.checkPaymentRequest(paymentRequest),
        checkRequired(elementOrSelectorToMountTo, 'elementOrSelectorToMountTo')
      );

      // Kick off initialization
      setupComponent.initialize();

      // Return the setup component to caller to make APIs available
      return setupComponent;
    } catch (err) {
      this.context.report({
        code: 'SETUP_COMPONENT_CREATION_ERROR',
        type: 'COMPONENT_CREATION_ERROR',
        message: `Error creating setup component: ${err}`,
      });
      throw err;
    }
  }

  /**
   * Constructs an express checkout component and mounts it in the DOM.
   * @param {Metadata} metadata - Payment method metadata. Since <code>4.0</code>.
   * @param {PaymentMethodSet} paymentMethodSet - Payment method set containing information about the payment methods to present, and their related vendor data.
   * @param {SalesforcePaymentsExpressConfig} config - Configuration of the checkout component.
   * @param {PaymentIntentRequest} paymentRequest - Request to make payment.
   * @param {string|HTMLElement} elementOrSelectorToMountTo - HTML element in which to mount the component, or a CSS selector matching that element.
   * @param {ExpressType} expressType - Express checkout use case.
   * @returns {Express} - New express component instance.
   * @memberof SFPayments
   * @instance
   * @public
   */
  express(
    metadata: Metadata,
    paymentMethodSet: PaymentMethodSet,
    config: SalesforcePaymentsExpressConfig,
    paymentRequest: PaymentIntentRequest,
    elementOrSelectorToMountTo: string | HTMLElement,
    expressType: ExpressType
  ): Express {
    try {
      const expressComponent = new Express(
        this.context,
        new MetadataReader(checkRequired(metadata, 'metadata')),
        checkRequired(paymentMethodSet, 'paymentMethodSet'),
        this.checkExpressConfig(config),
        this.checkPaymentIntentRequest(paymentRequest),
        checkRequired(elementOrSelectorToMountTo, 'elementOrSelectorToMountTo'),
        expressType
      );
      // kick off initialization
      expressComponent.initialize();

      return expressComponent;
    } catch (err) {
      this.context.report({
        code: 'EXPRESS_COMPONENT_CREATION_ERROR',
        type: 'COMPONENT_CREATION_ERROR',
        message: `Error creating express component: ${err}`,
      });
      throw err;
    }
  }

  messages(
    metadata: Metadata,
    paymentMethodSet: PaymentMethodSet,
    paymentRequest: PaymentIntentRequest,
    elementOrSelectorToMountTo: string | HTMLElement
  ): Messages {
    const messagesComponent = new Messages(
      this.context,
      new MetadataReader(checkRequired(metadata, 'metadata')),
      checkRequired(paymentMethodSet, 'paymentMethodSet'),
      null,
      this.checkPaymentIntentRequest(paymentRequest),
      checkRequired(elementOrSelectorToMountTo, 'elementOrSelectorToMountTo')
    );
    // TODO 244 implement initialize
    return messagesComponent;
  }

  /**
   * Handles an incoming redirect back from a 3rd party at which a payment was possibly confirmed. URL parameters
   * are used to identify the payment and its related vendor information. The response contains information obtained
   * from the vendor about the payment status, if available.
   * @returns {Promise<PaymentResponse>} - Response to the asynchronous payment confirmation.
   * @memberof SFPayments
   * @instance
   * @public
   */
  async handleRedirect(): Promise<PaymentResponse> {
    // when passing return url to the vendors, we append required information to params to delegate
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    try {
      const accountId = urlParams.get('accountId');
      const gatewayId = urlParams.get('gatewayId');
      const key = urlParams.get('key');
      const vendor = urlParams.get('vendor') as Vendor;
      const vendorAccount = {
        accountId: accountId,
        gatewayId: gatewayId,
        vendor: vendor,
        config: {
          key: key,
        },
      };
      const vendorHandler = await getVendorHandler(vendorAccount, this.context);
      let resp;
      if (urlParams.has('setup_intent_client_secret')) {
        resp = await vendorHandler.getSetupStatusForRedirect(urlParams);
      } else {
        resp = await vendorHandler.getPaymentStatusForRedirect(urlParams);
      }
      return resp;
    } catch (e) {
      console.log(e);
      this.context.report({
        code: 'HANDLE_REDIRECT_ERROR',
        vendor: urlParams.get('vendor') as Vendor,
      });
    }
  }

  private checkConfig<Type extends SalesforcePaymentsConfig>(config: Type): Type {
    checkRequired(config, 'config');
    checkRequired(config.actions, 'config.actions');
    checkRequired(config.options, 'config.options');
    return config;
  }

  private checkExpressConfig(config: SalesforcePaymentsExpressConfig): SalesforcePaymentsExpressConfig {
    this.checkConfig(config);
    if (
      config.options.maximumButtonCount != null &&
      (isNaN(config.options.maximumButtonCount) ||
        config.options.maximumButtonCount < 1 ||
        config.options.maximumButtonCount > 5)
    ) {
      throw new Error(`config.options.maximumButtonCount is not between 1 and 5`);
    }
    return config;
  }

  private checkPaymentRequest(paymentRequest: PaymentRequest): PaymentRequest {
    checkRequired(paymentRequest, 'paymentRequest');
    checkRequired(paymentRequest.locale, 'paymentRequest.locale');
    return paymentRequest;
  }

  private checkPaymentIntentRequest(paymentRequest: PaymentIntentRequest): PaymentIntentRequest {
    this.checkPaymentRequest(paymentRequest);
    checkRequired(paymentRequest.amount, 'paymentRequest.amount');
    checkAmount(paymentRequest.amount, 'paymentRequest.amount');
    checkRequired(paymentRequest.currency, 'paymentRequest.currency');
    checkCurrency(paymentRequest.currency, 'paymentRequest.currency');
    return paymentRequest;
  }
}
